import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '@tanstack/ai-react';
import type { UIMessage } from '@tanstack/ai-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAssistantScope, assistantScope } from './use-assistant-scope';
import {
  recoverConversation,
  reconcileOperation,
  type PendingOperation,
} from '../services/recovery-api';
import {
  AguiHttpError,
  createAguiConnection,
  type AguiSendContext,
} from '../services/agui-client';
import { useAssistantStore } from '../store/assistant-store';
import { readImportBatch } from '../helpers/import-display';
import { useConversationMessages } from './use-conversations';
import type { ConfirmationPayload } from '../types/agui.types';
import {
  isPresentationBlock,
  type PresentationBlock,
} from '../types/presentation.types';

export type ToolStatus =
  | 'ok'
  | 'denied'
  | 'error'
  | 'paused'
  | 'cancelled'
  | 'delivery_unknown'
  | 'partial_delivery';

export type AnswerPhase = 'checking' | null;

export interface ProgressNote {
  text: string;
  /** The id of the user message that started the turn the note belongs to. */
  turnId: string | null;
}

export type RunPhase = 'started' | 'tool' | 'text' | 'finished' | 'error';

/** What an approval may carry beyond the token (spec §7.3, imports only). */
export interface ApproveOptions {
  /** The draft's `import.rows[].row` numbers the user unticked. */
  excludedRows?: readonly number[];
}

/**
 * The server refused an approval before running it (a 400, e.g. "Nothing
 * left to import; cancel instead."). The card comes back showing `message`,
 * with the selection the user had (`excludedRows`) so they can adjust it.
 */
export interface ApprovalFailure {
  message: string;
  excludedRows?: number[];
  /** The refused draft: the failure belongs to this card only. */
  confirmationToken: number;
}

/** The latest run-lifecycle transition observed on the chat stream. */
export interface RunSignal {
  runId: string | null;
  phase: RunPhase | null;
  at: number;
}

/** Chunk types that mark a run-lifecycle transition, mapped to their phase. */
const CHUNK_PHASE: Partial<Record<string, RunPhase>> = {
  RUN_STARTED: 'started',
  TOOL_CALL_START: 'tool',
  TEXT_MESSAGE_CONTENT: 'text',
  RUN_FINISHED: 'finished',
  RUN_ERROR: 'error',
};

export interface UseAssistantChatReturn {
  unresolvedOperations: PendingOperation[];
  checkReceipt: (id: number) => Promise<void>;
  messages: UIMessage[];
  isLoading: boolean;
  /** The persisted-history fetch for this thread is still in flight. */
  historyLoading: boolean;
  error: Error | undefined;
  /** TOOL_CALL_END statuses by toolCallId; a started call with no entry is running. */
  toolStatuses: Record<string, ToolStatus>;
  /** Run-lifecycle signal: last phase transition + runId + timestamp. */
  runSignal: RunSignal;
  /** The pending Level-4 confirmation surfaced by the CUSTOM stream event. */
  confirmation: ConfirmationPayload | null;
  /**
   * The model's narration before each tool call ("Let me pull the invoices…"),
   * keyed by the toolCallId it introduces. Shown as a small progress line above
   * that chip — or, when the chip is not shown, as a line under the turn's
   * user message (`turnId`). Never taken back.
   */
  progressNotes: Record<string, ProgressNote>;
  /** 'checking' while the written answer is being checked, before it is shown. */
  answerPhase: AnswerPhase;
  /**
   * Answers that arrived whole after a check: the panel reveals each one with a
   * quick typing animation, once. A streamed answer is never re-typed.
   */
  revealIds: ReadonlySet<string>;
  /** Structured presentation blocks from CUSTOM `presentation` events, keyed by messageId. */
  presentationsByMessage: Map<string, PresentationBlock[]>;
  /** Fetch the next page of OLDER transcript and prepend it above the thread. */
  loadOlder: () => void;
  /** More transcript exists further back. */
  hasOlder: boolean;
  /** An older page is in flight. */
  loadingOlder: boolean;
  sendMessage: (text: string, files?: File[]) => Promise<void>;
  /**
   * Approve = the same /chat run resumed with the confirmation token. Rejects
   * (with the server's message) when the server refuses the approval with a
   * 400 before any stream starts; the confirmation is then restored.
   */
  approve: (options?: ApproveOptions) => Promise<void>;
  /** The last approval the server refused, shown on the restored card. */
  approvalFailure: ApprovalFailure | null;
  /** Reject = cancel the pending action server-side (throws on failure). */
  reject: () => Promise<void>;
}

/**
 * The assistant chat core on TanStack AI: one ChatClient per conversation
 * (keyed on the active conversation id), consuming the AG-UI stream from
 * /chat. Chat state lives in the SDK — the store only keeps
 * panel/session UI state.
 */
/** JSON with object keys sorted at every depth, so equal content gives an equal string. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(record[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

function stripCorrelationId(block: PresentationBlock): PresentationBlock {
  if (!('messageId' in block)) return block;
  const copy: Record<string, unknown> = { ...block };
  delete copy.messageId;
  return copy as unknown as PresentationBlock;
}

function canonicalBlockKey(block: PresentationBlock): string {
  return stableStringify(stripCorrelationId(block));
}

/**
 * The part of the SDK's list that persisted history has not covered yet — the
 * turn still in flight.
 *
 * Filtering by id alone kept an unmatched message wherever it sat, so one stale
 * copy from an earlier turn survived every reseed and was re-appended after the
 * whole transcript: the user saw their own older message at the bottom of the
 * thread with no reply under it, while the real reply sat further up on the
 * correctly seeded copy.
 *
 * History is authoritative up to its newest message, so anything BEFORE the last
 * seeded entry is stale by definition. Position rather than text, deliberately:
 * a user who sends "again" twice must keep both, and an in-flight turn is always
 * after the last seeded message.
 */
export function liveTail<T extends { id: string }>(
  sdkMessages: readonly T[],
  seededIds: ReadonlySet<string>
): T[] {
  let lastSeeded = -1;
  sdkMessages.forEach((message, index) => {
    if (seededIds.has(message.id)) lastSeeded = index;
  });
  return sdkMessages
    .slice(lastSeeded + 1)
    .filter((message) => !seededIds.has(message.id));
}

/**
 * Group a chronological message list into turns: a user message starts a new
 * turn, and everything after it (its replies, including any retracted draft)
 * belongs to that turn until the next user message.
 *
 * Used to carry a finished turn's tool-call chips across the history reseed
 * without matching by id — live ids are run ids and history ids are DB ids,
 * which are never the same value, but both lists agree on where one turn ends
 * and the next begins.
 */
export function splitIntoTurns<T extends { role: string }>(
  messages: readonly T[]
): T[][] {
  const turns: T[][] = [];
  let current: T[] = [];
  for (const message of messages) {
    if (message.role === 'user' && current.length > 0) {
      turns.push(current);
      current = [];
    }
    current.push(message);
  }
  if (current.length > 0) turns.push(current);
  return turns;
}

export function useAssistantChat(): UseAssistantChatReturn {
  const conversationId = useAssistantStore((s) => s.activeConversationId);
  const qc = useQueryClient();

  const scope = useAssistantScope();
  const historyId = useAssistantStore((s) => s.serverConversationId);

  // A thread must always have an id before the first send so the SDK client
  // (keyed on it) is never recreated mid-stream. startConversation assigns
  // one; this covers any entry path that left it null.
  useEffect(() => {
    if (!conversationId) {
      useAssistantStore.setState({ activeConversationId: crypto.randomUUID() });
    }
  }, [conversationId]);

  const [toolStatuses, setToolStatuses] = useState<Record<string, ToolStatus>>(
    {}
  );
  const [runSignal, setRunSignal] = useState<RunSignal>({
    runId: null,
    phase: null,
    at: 0,
  });
  const [confirmation, setConfirmationState] =
    useState<ConfirmationPayload | null>(null);
  const confirmationRef = useRef<ConfirmationPayload | null>(null);
  const setConfirmation = useCallback((c: ConfirmationPayload | null) => {
    confirmationRef.current = c;
    setConfirmationState(c);
  }, []);
  // An import approval the server refused (a 400 before any stream): the
  // card is back and shows it. Cleared by the next approve, reject or send.
  const [approvalFailure, setApprovalFailure] =
    useState<ApprovalFailure | null>(null);

  // Ids the server has retracted. Applied by the effect below rather than on
  // arrival — see the CUSTOM handler.
  const [superseded, setSuperseded] = useState<string[]>([]);
  const [finalMessages, setFinalMessages] = useState<Record<string, string>>(
    {}
  );
  const [receiptError, setReceiptError] = useState<Error>();
  const [progressNotes, setProgressNotes] = useState<
    Record<string, ProgressNote>
  >({});
  // The user message of the turn in flight (its id is the request id, which
  // is also the persisted row's id, so a note stays anchored after reload).
  const turnIdRef = useRef<string | null>(null);
  const [answerPhase, setAnswerPhase] = useState<AnswerPhase>(null);
  const [revealIds, setRevealIds] = useState<ReadonlySet<string>>(new Set());
  // Whether this run's answer went through the check (so it arrives whole and
  // is revealed), as opposed to a turn that streamed as it was written.
  const checkedRunRef = useRef(false);

  // Structured presentation blocks correlated to the assistant message they
  // belong to. Deduped by a structural key so a re-emitted event (same block
  // streamed twice) never double-renders.
  const [presentationsByMessage, setPresentationsByMessage] = useState<
    Map<string, PresentationBlock[]>
  >(new Map());
  const appendPresentation = useCallback(
    (messageId: string, incoming: PresentationBlock) => {
      // The same block reaches this hook up to three times per turn — as a
      // CUSTOM `presentation` (with `messageId` inside the value), inside
      // `final_message.blocks` (without it), and again from the persisted
      // history row, whose JSON key order differs. Identity is the block's
      // content, so the correlation id is stripped and the key is order-
      // independent; otherwise one table rendered three "View" chips.
      const block = stripCorrelationId(incoming);
      const key = canonicalBlockKey(block);
      setPresentationsByMessage((prev) => {
        const existing = prev.get(messageId) ?? [];
        if (existing.some((b) => canonicalBlockKey(b) === key)) return prev;
        const next = new Map(prev);
        next.set(messageId, [...existing, block]);
        return next;
      });
    },
    []
  );

  // Per-run request fields the connection adapter reads at connect() time.
  const sendCtxRef = useRef<Partial<AguiSendContext>>({});
  const sendError = useRef<Error | null>(null);

  const getSendContext = useCallback((): AguiSendContext => {
    const st = useAssistantStore.getState();
    return {
      conversationId: st.activeConversationId ?? '',
      serverConversationId: st.serverConversationId,
      ...sendCtxRef.current,
      ...(st.recordContext && !st.contextDismissed
        ? {
            recordContext: {
              type: st.recordContext.type,
              id: st.recordContext.id,
            },
          }
        : {}),
    };
  }, []);

  const connection = useMemo(
    () => createAguiConnection(getSendContext),
    [getSendContext]
  );

  const { messages, isLoading, error, append, setMessages } = useChat({
    id: `${scope}:${conversationId ?? 'new'}`,
    connection,
    onError: (failure) => {
      sendError.current = failure;
    },
    onChunk: (chunk) => {
      if (assistantScope() !== scope) return;
      // RUN_STARTED carries the conversation the server actually used —
      // the only way a brand-new thread learns its persisted id.
      if (chunk.type === 'RUN_STARTED' && 'conversationId' in chunk) {
        const serverId = (chunk as unknown as { conversationId?: number })
          .conversationId;
        if (serverId)
          useAssistantStore.getState().setServerConversationId(serverId);
      }
      const phase = CHUNK_PHASE[chunk.type];
      if (phase) {
        // RUN_STARTED carries the runId; later phases keep the current one.
        const chunkRunId =
          chunk.type === 'RUN_STARTED' && 'runId' in chunk
            ? ((chunk as unknown as { runId?: string }).runId ?? null)
            : undefined;
        setRunSignal((prev) => ({
          runId: chunkRunId !== undefined ? chunkRunId : prev.runId,
          phase,
          at: Date.now(),
        }));
      }
      if (chunk.type === 'RUN_STARTED') {
        // A run that died mid-check (no terminal frame) must not leave the next
        // turn saying "Checking the answer…" before it has written anything.
        checkedRunRef.current = false;
        setAnswerPhase(null);
      }
      if (chunk.type === 'TEXT_MESSAGE_START' && checkedRunRef.current) {
        // A checked answer arrives whole. Mark it for the reveal NOW, before its
        // bubble first renders: `final_message` comes after the text frames, and
        // a bubble that mounted without `reveal` would never type.
        const { messageId } = chunk as unknown as { messageId: string };
        setRevealIds((previous) =>
          previous.has(messageId) ? previous : new Set([...previous, messageId])
        );
      }
      if (
        chunk.type === 'TEXT_MESSAGE_START' ||
        chunk.type === 'RUN_FINISHED' ||
        chunk.type === 'RUN_ERROR'
      ) {
        setAnswerPhase(null);
      }
      if (chunk.type === 'TOOL_CALL_END' && 'status' in chunk) {
        const { toolCallId, status } = chunk as unknown as {
          toolCallId: string;
          status: ToolStatus;
        };
        setToolStatuses((prev) =>
          prev[toolCallId] === status ? prev : { ...prev, [toolCallId]: status }
        );
      }
    },
    onCustomEvent: (eventType, data) => {
      if (assistantScope() !== scope) return;
      if (eventType === 'final_message') {
        const final = data as {
          messageId: string;
          text: string;
          blocks: unknown[];
        };
        setFinalMessages((previous) => ({
          ...previous,
          [final.messageId]: final.text,
        }));
        setAnswerPhase(null);
        if (checkedRunRef.current) {
          setRevealIds((previous) =>
            previous.has(final.messageId)
              ? previous
              : new Set([...previous, final.messageId])
          );
        }
        final.blocks
          .filter(isPresentationBlock)
          .forEach((block) => appendPresentation(final.messageId, block));
      } else if (eventType === 'progress_note') {
        // Narration before a tool call. Not the answer, and never retracted.
        const note = data as { text: string; toolCallId: string };
        const turnId = turnIdRef.current;
        setProgressNotes((previous) =>
          previous[note.toolCallId]?.text === note.text
            ? previous
            : { ...previous, [note.toolCallId]: { text: note.text, turnId } }
        );
      } else if (eventType === 'answer_status') {
        checkedRunRef.current = true;
        setAnswerPhase('checking');
      } else if (eventType === 'confirmation_required') {
        setApprovalFailure(null);
        setConfirmation(data as ConfirmationPayload);
      } else if (eventType === 'presentation') {
        const block = data as PresentationBlock & { messageId?: string };
        if (block.messageId) appendPresentation(block.messageId, block);
      } else if (eventType === 'messages_superseded') {
        // Only a turn the server does not check streams as it is written; a
        // checked answer arrives whole. This is the server saying a streamed
        // draft did not survive the check after all (rare). Recorded as state rather than acted
        // on here: this fires mid-stream, when the SDK's message list has not yet
        // re-rendered with the replacement, so filtering now would drop the good
        // answer along with the bad one.
        const { messageIds } = data as { messageIds: string[] };
        setSuperseded((prev) => [...prev, ...messageIds]);
      }
    },
    onFinish: () => {
      qc.invalidateQueries({ queryKey: ['assistant'] });
    },
  });

  // Chat-turn state is per conversation — reset when the thread changes.
  useEffect(() => {
    setToolStatuses({});
    setConfirmation(null);
    setApprovalFailure(null);
    setRunSignal({ runId: null, phase: null, at: 0 });
    setPresentationsByMessage(new Map());
    setSuperseded([]);
    setFinalMessages({});
    setProgressNotes({});
    setAnswerPhase(null);
    setRevealIds(new Set());
    historicalChipsRef.current = new Map();
  }, [conversationId, scope, setConfirmation]);

  // Persisted history, newest page first, flattened oldest-first for display.
  const {
    messages: history,
    isLoading: historyLoading,
    loadOlder: fetchOlder,
    hasOlder,
    loadingOlder,
  } = useConversationMessages(historyId);

  // The SDK's list is [history][live turns]. Only the history prefix is
  // rewritten when an older page arrives, so a turn streaming right now is
  // never disturbed — hence the count, and the ref instead of a `messages`
  // dependency (which would re-run this on every delta).
  const seededIds = useRef(new Set<string>());
  const seededVersion = useRef('');
  const messagesRef = useRef<UIMessage[]>(messages);
  messagesRef.current = messages;

  // Tool-call chips carried from a live message into the seeded (text-only)
  // row that now represents its finished turn, keyed by that seeded row's id.
  // History never carries tool-call parts, so without this a chip shown while
  // a turn was live vanished the instant persisted history covered it — set
  // once per turn, it then rides along on every later reseed too.
  const historicalChipsRef = useRef(new Map<string, UIMessage['parts']>());

  useEffect(() => {
    const visible = history.filter(
      (m) => m.content !== '[confirmation requested]'
    );
    const seeded: UIMessage[] = visible.map((m, i) => ({
      id: m.id ?? `hist-${i}`,
      role: m.role,
      parts: [
        {
          type: 'text' as const,
          content: [
            m.content,
            ...(m.attachments ?? []).map((file) => `📎 ${file.name}`),
          ]
            .filter(Boolean)
            .join('\n\n'),
        },
      ],
    }));
    if (isLoading) return;
    const version = JSON.stringify(seeded);
    if (version === seededVersion.current) return;
    const nextIds = new Set(seeded.map((message) => message.id));
    // Keep only the TAIL the history has not covered yet — the turn still in
    // flight. Filtering by id alone kept any unmatched message wherever it sat,
    // so one stale copy from an earlier turn survived every reseed and was
    // re-appended after the whole transcript: the user saw their own older
    // message at the bottom of the thread with no reply under it (the reply was
    // further up, on the correctly seeded copy). History is authoritative for
    // everything up to its newest message, so anything in the SDK list BEFORE
    // the last seeded one is stale by definition. A genuine repeat of an
    // earlier message is still kept: an in-flight turn is always after it.
    const previousSeeded = seededIds.current;
    const live = liveTail(messagesRef.current, nextIds);

    // Everything that was outstanding as of the PREVIOUS reseed — a superset
    // of `live` in general, since some of it may have just been absorbed by
    // this one. Computed with the OLD seeded set so it still includes a
    // trailing message whose id happens to never show up in history (the
    // normal shape for an assistant reply: DB id vs run id, never equal).
    const previousLive = liveTail(messagesRef.current, previousSeeded);
    const previousLiveTurns = splitIntoTurns(previousLive);
    // Rows AFTER the newest previously-seeded row only — `loadOlder` can
    // prepend an older page onto the FRONT of `seeded` at any time (even
    // while a turn streams, deferred here by the `isLoading` guard above).
    // Those rows are unseen ids too, so filtering by "not in previousSeeded"
    // would wrongly count them as a newly-finished turn. `liveTail` already
    // finds the position right after the newest match, which a prepended
    // page — sitting before that position — is naturally excluded from.
    const newlySeeded = liveTail(seeded, previousSeeded);
    const newlySeededTurns = splitIntoTurns(newlySeeded);

    // History ids are DB ids, live ids are run ids — the two are never
    // matched by id, for the user row as much as the assistant's. What both
    // lists DO agree on is turn boundaries AND count: history always covers
    // the OLDEST outstanding turns first, one whole turn at a time, so the
    // oldest `newlySeededTurns.length` turns of `previousLiveTurns` are
    // exactly the ones this reseed just absorbed — pair turn-for-turn, never
    // index-for-index among a filtered list (a turn with no tool call, or an
    // extra tool-bearing message from a superseded draft, shifted every
    // pairing after it under the old approach). A turn count that doesn't
    // support this slice means we cannot tell which turn is which; skip the
    // carry-over entirely rather than guess.
    const absorbedIds = new Set<string>();
    if (previousLiveTurns.length >= newlySeededTurns.length) {
      const absorbedTurns = previousLiveTurns.slice(0, newlySeededTurns.length);
      absorbedTurns.forEach((turnMessages, i) => {
        turnMessages.forEach((message) => absorbedIds.add(message.id));
        // ALL of this turn's tool-call parts, including any from a draft the
        // server later retracted — the raw SDK list keeps retracted drafts
        // (see the superseded-handling effect below), so they are still here.
        const toolParts = turnMessages
          .filter((message) => message.role === 'assistant')
          .flatMap((message) =>
            message.parts.filter((p) => p.type === 'tool-call')
          );
        if (toolParts.length === 0) return; // a turn with no tool parts gets nothing
        const seededTurn = newlySeededTurns[i]!;
        const target = [...seededTurn]
          .reverse()
          .find((message) => message.role === 'assistant');
        if (!target) return;
        historicalChipsRef.current.set(target.id, toolParts);
      });
    }

    const seededWithChips = seeded.map((message) => {
      const chips = historicalChipsRef.current.get(message.id);
      return chips && chips.length > 0
        ? { ...message, parts: [...chips, ...message.parts] }
        : message;
    });
    // A message proven absorbed by turn-count stays out of the rendered
    // "still live" tail even though its id never matched anything in
    // history — otherwise it rendered forever as a trailing duplicate of the
    // seeded reply that now represents its turn.
    const liveAfterAbsorption = live.filter(
      (message) => !absorbedIds.has(message.id)
    );

    setMessages([...seededWithChips, ...liveAfterAbsorption]);
    seededIds.current = nextIds;
    seededVersion.current = version;
    // Persisted blocks re-attach to their message so a reload keeps its tables.
    history.forEach((m, i) => {
      const id = m.id ?? `hist-${i}`;
      (m.blocks ?? [])
        .filter(isPresentationBlock)
        .forEach((block) => appendPresentation(id, block));
    });
  }, [history, isLoading, setMessages, appendPresentation]);

  // Retracted drafts are hidden at RENDER time, never removed from the SDK's
  // list mid-run: calling setMessages while the stream is still delivering the
  // replacement raced the SDK's in-flight state and dropped the replacement —
  // the turn rendered blank (E2E 2026-08-17). The SDK list keeps the drafts;
  // only what this hook returns is filtered, and a conversation switch or
  // history reload rebuilds the list anyway.
  useEffect(() => {
    if (!superseded.length) return;
    setPresentationsByMessage((prev) => {
      if (!superseded.some((id) => prev.has(id))) return prev;
      const next = new Map(prev);
      superseded.forEach((id) => next.delete(id));
      return next;
    });
  }, [superseded]);

  const visibleMessages = useMemo(() => {
    const dropped = new Set(superseded);
    return messages
      .filter((m) => !dropped.has(m.id))
      .map((message) =>
        finalMessages[message.id] === undefined
          ? message
          : {
              ...message,
              parts: [
                { type: 'text' as const, content: finalMessages[message.id] },
              ],
            }
      );
  }, [messages, superseded, finalMessages]);

  const recovery = useQuery({
    queryKey: ['assistant', 'recovery', historyId, scope],
    queryFn: () => recoverConversation(historyId!),
    enabled: !!historyId && !isLoading,
    refetchInterval: (query) =>
      query.state.data?.runs.some((run) => run.status === 'RUNNING')
        ? 2000
        : false,
  });
  useEffect(() => {
    if (isLoading || !recovery.data) return;
    const pending = recovery.data.pending.find(
      (operation) =>
        operation.status === 'PENDING' &&
        Date.parse(operation.expires_at) > Date.now()
    );
    const latest = recovery.data.runs[0];
    if (pending && historyId) {
      // A refusal belongs to its own draft; a different pending action
      // starts without it.
      setApprovalFailure((failure) =>
        failure &&
        failure.confirmationToken !== pending.draft.confirmation_token
          ? null
          : failure
      );
      setConfirmation({ conversationId: historyId, draft: pending.draft });
    } else if (latest) setConfirmation(null);
    if (latest && latest.status !== 'RUNNING') {
      qc.invalidateQueries({
        queryKey: ['assistant', 'messages', historyId, scope],
      });
    }
  }, [recovery.data, isLoading, historyId, scope, qc, setConfirmation]);

  const loadOlder = useCallback(() => {
    if (hasOlder && !loadingOlder) fetchOlder();
  }, [hasOlder, loadingOlder, fetchOlder]);

  const sendMessage = useCallback(
    async (text: string, files: File[] = []) => {
      // Belt and braces: never send under a conversation that belongs to a
      // different user/org than this panel's.
      useAssistantStore.getState().bindScope(scope);
      // "Try again" after a stale thread id: the server no longer recognises
      // it for this user, so resending it can only fail the same way.
      const previousFailure = sendError.current;
      if (
        previousFailure instanceof AguiHttpError &&
        previousFailure.status === 400 &&
        /conversation not found/i.test(previousFailure.firstMessage)
      ) {
        useAssistantStore.setState({ serverConversationId: null });
      }
      if (!useAssistantStore.getState().activeConversationId) {
        useAssistantStore.setState({
          activeConversationId: crypto.randomUUID(),
        });
      }
      const requestId = crypto.randomUUID();
      turnIdRef.current = requestId;
      sendCtxRef.current = { message: text, requestId, files };
      sendError.current = null;
      setApprovalFailure(null);
      try {
        await append({
          id: requestId,
          role: 'user',
          parts: [
            {
              type: 'text',
              content: [text, ...files.map((file) => `📎 ${file.name}`)]
                .filter(Boolean)
                .join('\n\n'),
            },
          ],
        });
        // The SDK reports failures through onError but resolves append.
        // Preserve an attached file in the composer so the user can retry it.
        if (files.length && sendError.current) throw sendError.current;
      } finally {
        sendCtxRef.current = {};
      }
    },
    [append, scope]
  );

  const approve = useCallback(
    async (options: ApproveOptions = {}) => {
      const current = confirmationRef.current;
      if (!current) return;
      const excludedRows = options.excludedRows?.length
        ? [...options.excludedRows]
        : undefined;
      setConfirmation(null);
      setApprovalFailure(null);
      sendError.current = null;
      sendCtxRef.current = {
        requestId: crypto.randomUUID(),
        confirmationToken: current.draft.confirmation_token,
        ...(excludedRows ? { excludedRows } : {}),
      };
      turnIdRef.current = `resume-${current.draft.confirmation_token}`;
      try {
        // A resumed run with no user text: append a part-less user message so
        // the SDK starts a stream without rendering a bubble.
        await append({
          id: `resume-${current.draft.confirmation_token}`,
          role: 'user',
          parts: [],
        });
      } finally {
        sendCtxRef.current = {};
      }
      // An IMPORT approval refused with a 400 before any stream: nothing ran,
      // the action is still pending. Put the card back with the server's
      // words and the user's selection (spec §7.3). Any other card keeps the
      // old behaviour: it goes, and the panel shows the error.
      //
      // Relies on the SDK's error ordering (@tanstack/ai-client): when
      // connect() throws, connection-adapters.js's catch pushes a synthetic
      // RUN_ERROR (built from the message only) into the subscription buffer
      // and rethrows the ORIGINAL error; chat-client.js catches that rethrow
      // and calls reportStreamError(original) before the buffered frame is
      // processed, and reportStreamError reports once per stream generation.
      // So onError — `sendError` — holds our AguiHttpError. If that order
      // changes, this check stops matching and an import refusal degrades to
      // the old panel error (covered by the hook tests).
      const failure = sendError.current as Error | null;
      if (
        failure instanceof AguiHttpError &&
        failure.status === 400 &&
        failure.firstMessage &&
        readImportBatch(current.draft.import) !== null
      ) {
        setConfirmation(current);
        setApprovalFailure({
          message: failure.firstMessage,
          confirmationToken: current.draft.confirmation_token,
          ...(excludedRows ? { excludedRows } : {}),
        });
        throw new Error(failure.firstMessage);
      }
    },
    [append, setConfirmation]
  );

  const reject = useCallback(async () => {
    const current = confirmationRef.current;
    if (!current) return;
    setConfirmation(null);
    setApprovalFailure(null);
    // Rejection resumes the same run with approved=false: the backend settles the
    // pending action AND closes the paused tool call, then streams an acknowledgment.
    sendCtxRef.current = {
      requestId: crypto.randomUUID(),
      confirmationToken: current.draft.confirmation_token,
      approved: false,
    };
    turnIdRef.current = `reject-${current.draft.confirmation_token}`;
    try {
      await append({
        id: `reject-${current.draft.confirmation_token}`,
        role: 'user',
        parts: [],
      });
    } finally {
      sendCtxRef.current = {};
    }
  }, [append, setConfirmation]);

  return {
    unresolvedOperations: (recovery.data?.pending ?? []).filter((operation) =>
      ['EXECUTING', 'UNKNOWN', 'PARTIAL'].includes(operation.status)
    ),
    checkReceipt: async (id: number) => {
      try {
        setReceiptError(undefined);
        await reconcileOperation(id);
        await recovery.refetch();
      } catch (failure) {
        setReceiptError(
          failure instanceof Error
            ? failure
            : new Error('The receipt could not be checked.')
        );
      }
    },
    messages: visibleMessages,
    isLoading,
    historyLoading,
    // While a refused import approval is on its restored card, the card shows
    // the message; the panel does not repeat it. The SDK clears `error` when
    // the next stream starts, and every new run clears approvalFailure.
    error:
      (approvalFailure && confirmation ? undefined : error) ?? receiptError,
    toolStatuses,
    runSignal,
    confirmation,
    presentationsByMessage,
    progressNotes,
    answerPhase,
    revealIds,
    loadOlder,
    hasOlder: !!hasOlder,
    loadingOlder,
    sendMessage,
    approve,
    approvalFailure,
    reject,
  };
}
